// Wiring for the standalone /parquet-viewer page: pickers, drop zone, errors and
// the mounted viewer. Everything about *reading* Parquet comes from the shared
// loader (lib/document-load.js) and the shared viewer (ui/file-viewer.js), so
// this surface can never diverge from the comparison page's rules (G51).
import { createDocumentLoader, documentLabel } from '../lib/document-load.js';
import { readBinary } from '../lib/worker-client.js';
import { mountFileViewer } from './file-viewer.js';
import { showNotice } from './notice-dialog.js';

const $ = (s) => document.querySelector(s);

// `report` keeps this layer analytics-free in the same way ui/input.js does: the
// page composition passes `track`, tests pass a spy, and nothing here decides what
// an event name is. Only shapes and bounded reasons are ever reported — never a
// file name, a path, a row count or an error message.
export function initViewerPage({ readBinary: read = readBinary, report = () => {} } = {}) {
  const els = { error: $('#viewerError'), mount: $('#viewerMount'), name: $('#viewerFile'), clear: $('#viewerClear'), drop: $('#viewerDrop'), page: $('.viewer-page') };
  let unmount = () => {};
  let current = null;

  // Progress stays inline; a refusal is a notice the reader acknowledges, the same
  // dialog the comparison page uses. The previous dataset stays mounted either way.
  const setProgress = (message) => {
    els.error.textContent = message;
    els.error.hidden = false;
  };
  const refuse = (message, oversize) => {
    clearError();
    showNotice({ title: oversize ? 'Too large to open' : 'Couldn’t open that selection', message });
  };
  const clearError = () => {
    els.error.textContent = '';
    els.error.hidden = true;
  };

  // ONE selection, so the budget and its wording are a single dataset's — never
  // a two-pane comparison's.
  const loader = createDocumentLoader({
    readBinary: read,
    only: 'parquet',
    singleBudget: true,
    onStatus: (status) => {
      if (status.state === 'error' && status.reason) report('viewer_reject', { kind: status.reason });
      if (status.state === 'idle') clearError();
      else if (status.state === 'busy') setProgress(status.message);
      else refuse(status.message, status.reason === 'oversize');
    },
  });

  // A rejected or superseded load leaves the previous complete dataset mounted.
  function apply(result) {
    if (!result) return;
    unmount();
    current = result.document;
    els.mount.hidden = false;
    els.clear.hidden = false;
    els.name.textContent = documentLabel(result);
    // The upload screen is sized to fill the viewport while empty; once a dataset
    // is open it collapses so the table is not pushed below the fold.
    els.page?.classList.add('has-document');
    // The page becomes a workbench while a dataset is open: the grid takes the
    // viewport, and the marketing prose steps aside until the file is closed.
    document.body.classList.add('viewer-open');
    report('viewer_open', { kind: result.directory ? 'folder' : result.entries?.length > 1 ? 'multi' : 'single' });
    unmount = mountFileViewer(els.mount, result.document, {
      variant: 'workbench',
      onClose: clear,
      onExport: (scope) => report('viewer_export', { kind: scope }),
    });
  }

  function clear() {
    loader.invalidate();
    unmount();
    unmount = () => {};
    current = null;
    els.mount.hidden = true;
    els.clear.hidden = true;
    els.name.textContent = '';
    els.page?.classList.remove('has-document');
    document.body.classList.remove('viewer-open');
    clearError();
  }

  const load = async (files, options) => apply(await loader.load(files, options));

  els.clear.addEventListener('click', clear);

  for (const btn of document.querySelectorAll('.viewer-actions .upload-btn')) {
    const input = document.getElementById(btn.dataset.input);
    btn.addEventListener('click', () => input.click());
    input.addEventListener('change', (e) => {
      const files = [...e.target.files];
      e.target.value = ''; // allow re-opening the same selection
      if (files.length) load(files, { directory: input.hasAttribute('webkitdirectory') });
    });
  }

  els.drop.addEventListener('dragover', (e) => {
    e.preventDefault();
    els.drop.classList.add('dragover');
  });
  els.drop.addEventListener('dragleave', (e) => {
    if (!els.drop.contains(e.relatedTarget)) els.drop.classList.remove('dragover');
  });
  els.drop.addEventListener('drop', async (e) => {
    e.preventDefault();
    els.drop.classList.remove('dragover');
    apply(await loader.loadDrop(e.dataTransfer));
  });

  return { load, clear, current: () => current, isLoading: () => loader.isLoading() };
}
